import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { submitPartnerLead } from '../services/partnerLead';
const gtcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const gtcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const gtcPrograms = [{
  slug: 'diploma-business-management',
  title: 'Diploma in Business Management',
  shortTitle: 'Business Management',
  mProgram: '63926'
}, {
  slug: 'diploma-computer-science',
  title: 'Diploma in Computer Science',
  shortTitle: 'Computer Science',
  mProgram: '63928'
}, {
  slug: 'aab-business-management',
  title: 'Associate of Applied Business (A.A.B.) in Business Management',
  shortTitle: 'A.A.B. Business Management',
  mProgram: '63927'
}, {
  slug: 'aas-computer-science',
  title: 'Associate of Applied Science (A.A.S.) in Computer Science',
  shortTitle: 'A.A.S. Computer Science',
  mProgram: '63929'
}];
export function GTCLandingPage() {
  usePageMeta('Global Tech College | Career-Focused Degree Programs', "Explore online degree programs at Global Tech College. Career-focused degree and certificate programs designed to prepare students for success in today's competitive job market.", {
    ogImage: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = gtcPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await submitPartnerLead('gtc', params);
      } catch (err) {
        console.error('Partner lead submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Global Tech College',
            logo: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
            primaryColor: '#1B365D',
            accentColor: '#8B1A1A',
            backUrl: '/colleges/global-tech-college',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const currentProgramTitle = gtcPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1661" data-mp="11jw8met70yws">
          <div data-id="element-1662" data-mp="11gs23as99r2y">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1663" data-mp="16hfo1i1mnhqb">
              Select a Program
            </label>
            <div className="relative" data-id="element-1664" data-mp="sk96fqsn3p2o">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#1B365D] focus:border-[#1B365D] font-medium text-gray-800" data-id="element-1665" data-mp="wiqeqdnsr5lo">
                <option value="" data-id="element-1666" data-mp="cs670vmzdqhg">-- Choose a Program --</option>
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1667" data-mp="7t33xgqk9uc4">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1668" data-mp="3s116pxfr05e">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1669" data-mp="3uzdj57657hmd" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-1670" data-mp="vhmczyhfqb59">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1671" data-mp="mosqbfzns9hd">
          <div data-id="element-1672" data-mp="mnr2sc1ghuir">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1673" data-mp="hr91jm5c4eb6">
              Program
            </label>
            <div className="relative" data-id="element-1674" data-mp="1f7pq3mu9298u">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1675" data-mp="1afy31maaylng">
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1676" data-mp="1ms09riby6qek">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1677" data-mp="1vr3sgl77d3sj">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1678" data-mp="20qgnht889h67" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1679" data-mp="25nqehdb9m0w5" />

          <div data-id="element-1680" data-mp="2afi33iav1wjn">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1681" data-mp="2aeynw9clhm9b">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1682" data-mp="2jggcyh7zcya5">
              <option value="" data-id="element-1683" data-mp="1xriunrmsxqfs">-- Please Select --</option>
              {gtcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1684" data-mp="22qdemwwi9l1h">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1685" data-mp="27o5gojqv6nje">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1686" data-mp="2ghs7rwk4n2m9">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1687" data-mp="2gia2upv1zasl">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1688" data-mp="2qg7nz4hm8rgz">
              <option value="" data-id="element-1689" data-mp="2vcyeymuyl7h6">-- Please Select --</option>
              {gtcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1690" data-mp="w3rm0fvurzhx">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1691" data-mp="3ebt3v12dl2vn">{errors.startDate}</p>}
          </div>

          <div data-id="element-1692" data-mp="39k1egtuyh373">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1693" data-mp="39kb8csj4fui2">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1694" data-mp="30kenz33rlhst">
              <option value="" data-id="element-1695" data-mp="12nblcogrf39">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1696" data-mp="3t9brg03bha1i">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1697" data-mp="3jacrr70j4oqn">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1698" data-mp="2cuajyumnzg1a">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1699" data-mp="2cvbtguo17ugw">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1700" data-mp="29aa1zzlq734m" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1701" data-mp="2e9d9dqp6hlfr">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1702" data-mp="1vcv7whkj276b">
          <div data-id="element-1703" data-mp="1vdwnmzchnhud">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1704" data-mp="1veg5yh61w88x">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1705" data-mp="1lnljpcst18nh" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1706" data-mp="o2iutknfa3ms">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1707" data-mp="12zjc0u7zmg70">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1708" data-mp="xyvowjmvqji2" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1709" data-mp="1703ac14arpuq">
                <div data-id="element-1710" data-mp="16zblra94asdy">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1711" data-mp="16y0n1ouj4atp">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="City" data-id="element-1712" data-mp="1gr7ncvaex0mx" />
                </div>
                <div data-id="element-1713" data-mp="3k69x7ids482p">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1714" data-mp="3k4p0pn7wmf27">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="State" data-id="element-1715" data-mp="3tx4junuxt9oo" />
                </div>
              </div>
              <div data-id="element-1716" data-mp="368v2by4rb83c">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1717" data-mp="3b6d6zcbqr15j">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`} placeholder="123 Main St" data-id="element-1718" data-mp="3f5dmakv0flns" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1719" data-mp="266evrpeac073">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1720" data-mp="2172a37z5gz63">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1721" data-mp="2160xgz2hk4m9">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1722" data-mp="1n7u8y9jffgz9" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1723" data-mp="1i9sobk6cr9lo">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1724" data-mp="3vqm1fmmhpfsk">
          <div className="grid grid-cols-2 gap-3" data-id="element-1725" data-mp="4czie6lg5wen">
            <div data-id="element-1726" data-mp="4ess5urcwwqv">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1727" data-mp="4fc50x25guy1">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1728" data-mp="3rqtoyytz6phx" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1729" data-mp="n9zkspnpgd2c">{errors.firstName}</p>}
            </div>
            <div data-id="element-1730" data-mp="9c2jjfyb3ihc">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1731" data-mp="9eowjv3yuq6y">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1732" data-mp="2u0ytkxjm2ouj" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1733" data-mp="2rk83jpcxq1dt">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1734" data-mp="2mld8np1p0mdc">
            <div data-id="element-1735" data-mp="30hiflcxktujs">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1736" data-mp="30i0ksz27vj3u">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1737" data-mp="27v62a7oymtt0" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1738" data-mp="2hkplxfkhyrox">{errors.email}</p>}
            </div>
            <div data-id="element-1739" data-mp="2cs66ed291mhh">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1740" data-mp="2cro16qxlzxxf">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1741" data-mp="1k5bnhkl2yv64" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1742" data-mp="13klg4bnqo5gc">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D] cursor-pointer flex-shrink-0" data-id="element-1743" data-mp="18lhmqwzh65pg">
            By clicking the Request Info button, you agree to be contacted by
            phone, text message, or email via automated systems by Global Tech
            College about your education at the phone numbers you provided
            above, including any wireless number(s). You are not obligated to
            agree to automated contact to enroll. Note that even non-automated
            calls are recorded for quality assurance.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1744" data-mp="3qpqxm7l5iisb">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1745" data-mp="3qqisthau6ei2">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1746" data-mp="3bs6tt3zzqtnq" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#8B1A1A] to-[#A52A2A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1747" data-mp="dab9bytrgj9u">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1748" data-mp="3uoq5o126ivnz" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1749" data-mp="r89bm5ut4p8z">
      {/* A. Custom Header */}
      <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1750" data-mp="2hsk2vz84jvcv">
        <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt="Global Tech College Logo" className="h-16 object-contain" data-id="element-1751" data-mp="2cy6bs90hwae0" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-br from-[#1B365D] to-[#8B1A1A] relative overflow-hidden" data-id="element-1752" data-mp="30leh2qxxbjg1">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1753" data-mp="2wjswskj282vt" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-1754" data-mp="3k9eypfqtgw7i">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-1755" data-mp="3fgu5ogfqin51">
            <p className="text-[#D4A843] font-medium mb-3 text-lg tracking-wide" data-id="element-1756" data-mp="3muuye5yt9irj">
              Global Tech College Online<sup className="text-xs" data-id="element-1757" data-mp="3rtpl3izwfrgx">1</sup>
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-1758" data-mp="m3k5y4ke33r">
              {gtcPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Career-Focused
                  <br data-id="element-1759" data-mp="9mrw8naedte0" />
                  Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-1760" data-mp="el35avsvbzv6">
              Global Tech College offers career-focused degree and certificate
              programs designed to prepare students for success in today's
              competitive job market. With flexible online options and dedicated
              support, we help you achieve your educational goals.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-1761" data-mp="xaj85ps104er">
            <form onSubmit={e => e.preventDefault()} id="gtc-lead-form" data-id="element-1762" data-mp="2pfqdibgnwepm">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1763" data-mp="337wprmgp21js" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1764" data-mp="2yd0nlthqtxsy" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1765" data-mp="3d26ix6dgn3vk">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1766" data-mp="3m3ee3fkojolf">
                <div className="bg-gradient-to-r from-[#1B365D] to-[#2A528A] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1767" data-mp="3h6wfkxfy0mhj"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1768" data-mp="1mtog2sh0vmpk">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1769" data-mp="2918c3v9ko1u">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-gray-50 py-12 border-b border-gray-200" data-id="element-1770" data-mp="3kp3ne3727x1g">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1771" data-mp="36oj4l715a17y">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1772" data-mp="3apcs3zaode0b">
            <div className="flex flex-col items-center text-center" data-id="element-1773" data-mp="3apuu7ck0qmg5">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1774" data-mp="14w4li4mv6qpy">
                <LaptopIcon className="w-8 h-8" data-id="element-1775" data-mp="18xg6txypgpsc" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1776" data-mp="k7lonw3bpisd">
                100% Online Flexibility
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1777" data-mp="f8s63p96j1lc">
                Study on your schedule with 24/7 access to coursework.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1778" data-mp="5aksnge3n1o3">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1779" data-mp="5cfdyvta8vsp">
                <BriefcaseIcon className="w-8 h-8" data-id="element-1780" data-mp="3smlsyufxfcjw" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1781" data-mp="3slapywub28tu">
                Career-Focused Curriculum
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1782" data-mp="3iphezhw8ine6">
                Gain practical skills designed for today's job market.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1783" data-mp="3drz3pkuqvw30">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1784" data-mp="39pkkgzn5yub7">
                <AwardIcon className="w-8 h-8" data-id="element-1785" data-mp="1p8bfhkdze6vg" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1786" data-mp="1t6sknydud7a8">
                Accredited Institution
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1787" data-mp="1y7q228r11xao">
                Accredited by ACCET, ensuring quality education standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Section */}
      <section className="py-16 bg-white" data-id="element-1788" data-mp="1ah2szk5dl1mj">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1789" data-mp="1ahvsc0208oun">
          <h2 className="text-3xl font-bold text-[#1B365D] mb-6" data-id="element-1790" data-mp="1ffe561j57o7x">
            Why Choose Global Tech College?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1791" data-mp="1k8qez5j5xqgm">
            At Global Tech College, we are committed to providing accessible,
            high-quality education that empowers students to achieve their
            professional goals. Our programs are designed with input from
            industry experts to ensure you learn the skills employers are
            looking for right now.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 text-left border border-gray-100" data-id="element-1792" data-mp="fmh54tr57nyp">
            <h3 className="text-xl font-bold text-[#1B365D] mb-4" data-id="element-1793" data-mp="tf6qcvyukour">
              Student Support Services
            </h3>
            <ul className="space-y-3" data-id="element-1794" data-mp="raxy3gb9j3s">
              <li className="flex items-start" data-id="element-1795" data-mp="scaydgw0fo5">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1796" data-mp="t465n6kobdw" />
                <span className="text-gray-700" data-id="element-1797" data-mp="3sxg6e4354829">
                  Live Tutor support via email & chat
                </span>
              </li>
              <li className="flex items-start" data-id="element-1798" data-mp="5q4bfvvlla3y">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1799" data-mp="5sgukcd3dqil" />
                <span className="text-gray-700" data-id="element-1800" data-mp="1m6qhr35ll97u">
                  Dedicated academic advisors to guide your journey
                </span>
              </li>
              <li className="flex items-start" data-id="element-1801" data-mp="2z24go87iip30">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1802" data-mp="2z5ilro7k9ce5" />
                <span className="text-gray-700" data-id="element-1803" data-mp="33zw87148w96o">
                  Career services assistance including resume building and
                  interview prep
                </span>
              </li>
              <li className="flex items-start" data-id="element-1804" data-mp="2v3opds4eeujs">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1805" data-mp="3iqwua8xx01la" />
                <span className="text-gray-700" data-id="element-1806" data-mp="3mt064yohx873">
                  Flexible learning with pre-recorded and live lectures
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* E. Footer */}
      <footer className="bg-[#1B365D] text-white/70 py-12 text-xs" data-id="element-1807" data-mp="3dybyx2zd1gfn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4" data-id="element-1808" data-mp="5jv2esip0uof">
          <p data-id="element-1809" data-mp="acepcg39t25w">
            Global Tech College is accredited by The Accrediting Council for
            Continuing Education & Training (ACCET).
          </p>
          <p data-id="element-1810" data-mp="21nrn3cbxgwlk">
            <sup className="text-[10px]" data-id="element-1811" data-mp="21o1caxp5ezq7">1</sup> Online programs are subject to
            state availability. Not all programs are available in all states.
          </p>
          <p className="pt-4 border-t border-white/10" data-id="element-1812" data-mp="1wmlq32b8in68">
            © {new Date().getFullYear()} Global Tech College. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>;
}